import type { IActionGameConfig, ITerraformingRuntimeConfig } from '../../engine-game-config';
import type { ActionGamePresentationConfig } from '../../entities/bot-presentation';
import type { GridSkillCastVisibilitySettings } from '../../grid-skill-cast';
import type { TileVisibilitySampleDensity } from '../../grid-visibility';
import type { NavMeshWireSnapshot } from '../../match-init/nav-mesh-wire';

import type { TerraformingMapGenDebug } from './terraforming-map-gen-debug';

/** Maximum presentation-grid cells addressable by the 16-bit live terrain-delta wire format. (65536 or 256^2) */
export const TF_TERRAIN_DELTA_MAX_TILE_COUNT = 0x10000;

/**
 * Shared numeric stages for the plantable surface track (schema fields, bootstrap arrays, AGE state).
 *
 * Valid pairings with {@link ObstacleTileStage}:
 * - `None` + `None` — POI / inert tile (basecamp, seed stations, seed pads; no walking flip, no obstacle progression).
 * - `Open` / `Grass` / `Flower` + `None` — growable track (walk to plant, progress toward grass goal).
 * - `None` + `Sprout` / `Sapling` / `Tree` — obstruction track (match `obstacleTier` applies walking/burst rules).
 *
 * Any other combination is invalid.
 */
export enum TerrainSurfaceStage {
  None = 0,
  Open = 1,
  Grass = 2,
  Flower = 3
}

/**
 * Obstruction growth for per-tile schema fields and match-wide `obstacleTier`.
 *
 * Sprout is walkable. Sapling blocks walking. Tree blocks walking and seed effects.
 * See {@link TerrainSurfaceStage} for valid tile pairings. None is tile-only (POI).
 */
export enum ObstacleTileStage {
  None = 0,
  Sprout = 1,
  Sapling = 2,
  Tree = 3
}

/** Returns whether `surfaceStage` and `obstacleStage` form a legal tile (POI, growable, or obstacle). */
export function isValidTerrainTileStage(surfaceStage: TerrainSurfaceStage, obstacleStage: ObstacleTileStage): boolean {
  if (obstacleStage !== ObstacleTileStage.None) {
    return surfaceStage === TerrainSurfaceStage.None;
  }

  return true;
}

export interface TerrainProgressState {
  totalGrowableTiles: number;
  grassTiles: number;
  flowerTiles: number;
  grassCoverageRatio: number;
  grassGoalProgressRatio: number;
  flowersOnGoalGrassRatio: number;
}

/** TF pathing mesh ids — one source of truth for AGE register keys and bootstrap Record keys. */
export const terraformingNavMeshIds = {
  open: 'open',
  blocked: 'blocked'
} as const;

export type TerraformingNavMeshId = (typeof terraformingNavMeshIds)[keyof typeof terraformingNavMeshIds];

export function isTerraformingNavMeshId(value: unknown): value is TerraformingNavMeshId {
  return value === terraformingNavMeshIds.open || value === terraformingNavMeshIds.blocked;
}

/** One TF pathing mesh on the wire (boundary + holes + triangulation). */
export type TerraformingNavMeshSnapshot = NavMeshWireSnapshot;

/**
 * TF pathing meshes keyed by {@link TerraformingNavMeshId}.
 * Clients register every entry on one runtime and activate open until gameplay or debug switches.
 */
export type TerraformingPathingNavMeshes = Record<TerraformingNavMeshId, TerraformingNavMeshSnapshot>;

export interface TerraformingTerrainSnapshot {
  mapSeed: string;
  widthTiles: number;
  heightTiles: number;
  revision: number;
  stagesByTileIndex: number[];
  growableTileIndices: number[];
  progressTileIndices: number[];
  obstructionTileIndices: number[];
  /** One-tile outer ring (POI on wire; not walkable / not in progress). */
  borderTileIndices: number[];
  /** Full basecamp footprint including seed-station tiles (POI on wire). */
  basecampTileIndices: number[];
  seedBurstPadTileIndices: number[];
  seedStationTileIndices: number[];
  /** Config playable size (WYSIWYG with procedural map config). */
  playableWidthTiles: number;
  playableHeightTiles: number;
  /** Presentation ring outside playable (from procedural `presentation.borderOutsetTiles`, default 1). */
  borderOutsetTiles: number;
}

export interface SeedBurstPadState {
  /** Reference tile for the pad footprint; not necessarily geometric center (e.g. 4×4 pads anchor on one corner tile). */
  anchorTileIndex: number;
  footprintTileIndices: number[];
  radiusPx: number;
  chargeRatio: number;
  active: boolean;
  playerCount: number;
  firedCount: number;
}

export interface GuideTargetState {
  playerId: string;
  targetKind: number;
  tileIndex: number;
  revision: number;
}

/** Client reveal-wave knobs for skill/pad plants (AGE always sends those plants as reveal groups). */
export interface TfTileRevealStaggerConfig {
  seedPadBurstEnabled: boolean;
  grassBurstEnabled: boolean;
  flowerWaveEnabled: boolean;
  progressCounterSync: boolean;
  /** Delay between consecutive Chebyshev distance rings. */
  stepMs: number;
}

/** Client knobs for the RE obstruction growth cinematic (letterbox, jitter, art stagger, zoom). */
export interface TfObstacleGrowthPresentationSettings {
  /** Hold after letterbox close before growth art starts (local flair; does not replace AGE lock). */
  leadInMs: number;
  letterboxCloseMs: number;
  letterboxOpenMs: number;
  jitterLowPx: number;
  jitterGrowthPx: number;
  /**
   * Max random delay (ms) before each in-view growth atlas clip starts.
   * 0 starts every clip together; clip length is unchanged.
   */
  artStaggerMsMax: number;
  /** Extra tiles left/right of the camera for side canopy hangover. */
  artMarginTilesLateral: number;
  /** Extra tiles below the camera for canopy hangover into view. */
  artMarginTilesSouth: number;
  /**
   * Extra zoom-in while letterbox bars are closed, as a fraction of the current camera scale.
   * 0 = no zoom change. 0.1 = 10% closer. Restores to the pre-letterbox scale when bars open.
   */
  letterboxZoomIn: number;
}

/**
 * Soft alpha falloff for interior trees and bottom/flank border trees near the local (or ally) bot.
 * Top-row border trees stay opaque. The same `enabled` switch covers interior trees and those border trees.
 * Distance is bot world position to the object's bottom-center; radius is `radiusTiles * tilePx`.
 */
export interface TfObstructionProximityFadeSettings {
  enabled: boolean;
  /** Fade radius in tiles (converted with tilePx at runtime). */
  radiusTiles: number;
  /** Alpha at distance 0; lerps to 1.0 at {@link radiusTiles}. */
  centerAlpha: number;
  /** When false, only the local player's bot drives fade. */
  applyForAllyBots: boolean;
  /** Obstacle stages that participate (bootstrap/JSON must supply the list; Tree-only is the usual authoring choice). */
  stages: ObstacleTileStage[];
}

/**
 * Client bootstrap presentation: tile reveal, growth cinematic, proximity fade, grass-solid min stage, and grass aim delay.
 * {@link obstructionGrassSolidMinStage} is the lowest obstacle stage that may earn RE filled-center grass (when it has no Open AGE-growable Moore neighbors).
 * Border tiles are always filled grass. Any filled tile drives edge frames on every adjacent tile.
 * {@link ObstacleTileStage.None} disables interior obstruction RE-grass only.
 */
export interface TerraformingClientPresentation {
  tileRevealStagger: TfTileRevealStaggerConfig;
  obstacleGrowth: TfObstacleGrowthPresentationSettings;
  obstructionProximityFade: TfObstructionProximityFadeSettings;
  obstructionGrassSolidMinStage: ObstacleTileStage;
  /**
   * Milliseconds a grass press waits before the aim overlay appears.
   * A shorter release casts and leaves the overlay hidden. 0 shows the overlay on press.
   */
  grassAimPreviewDelayMs: number;
}

/** AGE merged TF presentation: shared bot flags plus {@link TerraformingClientPresentation}. */
export interface TerraformingPresentationConfig
  extends ActionGamePresentationConfig, TerraformingClientPresentation {}

/** Full config for terraforming matches (base + mode fields, after merge). */
export interface ITerraformingGameConfig extends IActionGameConfig {
  runtime: ITerraformingRuntimeConfig;
  /** TF presentation includes shared bot flags, tile-reveal stagger, obstacle growth, proximity fade, grass-solid min stage, and the grass aim delay. */
  presentation: TerraformingPresentationConfig;
  /** Min fraction of a candidate tile's samples that must be visible for the tile to flip. */
  tileVisibilityThreshold: number;
  /** Number and layout of sample points tested on each candidate tile. */
  tileVisibilitySampleDensity: TileVisibilitySampleDensity;
  /** Whether seed-pad bursts use the same tile-visibility filter as player skills. */
  seedPadBurstsUseTileVisibility: boolean;
}

export interface TerraformingClientBootstrapBase {
  obstacleTier: ObstacleTileStage;
  gridSkillCastVisibility: GridSkillCastVisibilitySettings;
}

/** Full authoritative preload payload for TF clients and late join. */
export interface TerraformingClientBootstrap extends TerraformingClientBootstrapBase {
  presentation: TerraformingClientPresentation;
  terrain: TerraformingTerrainSnapshot;
  progress: TerrainProgressState;
  guideTargets: GuideTargetState[];
  /** Open + Blocked pathing for remote prediction and overlays (same ids as AGE runtime). */
  pathingNavMeshes: TerraformingPathingNavMeshes;
  /** Which of {@link pathingNavMeshes} is live for pathing. */
  activeNavMesh: TerraformingNavMeshId;
  /**
   * Layout-generation audit for the TF match debug panel.
   * Present only when the engine ran with `debugMode` (see {@link TerraformingMapGenDebug}).
   */
  mapGenDebug?: TerraformingMapGenDebug;
}
